// Service Worker registration and management utilities

interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOfflineReady?: () => void;
  onNeedRefresh?: (registration: ServiceWorkerRegistration) => void;
}

class ServiceWorkerManager {
  private config: ServiceWorkerConfig;
  private registration: ServiceWorkerRegistration | null = null;

  constructor(config: ServiceWorkerConfig = {}) {
    this.config = config;
  }

  // Register service worker
  async register(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.log('🚫 Service Worker not supported');
      return;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'imports'
      });

      this.registration = registration;
      console.log('✅ Service Worker registered:', registration.scope);

      // Handle different registration states
      if (registration.installing) {
        this.trackInstalling(registration.installing);
      } else if (registration.waiting) {
        this.config.onNeedRefresh?.(registration);
      } else if (registration.active) {
        this.config.onSuccess?.(registration);
      }

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        console.log('🔄 Service Worker update found');
        const newWorker = registration.installing;
        if (newWorker) {
          this.trackInstalling(newWorker);
        }
      });

      // Check for updates periodically (every 1 hour)
      setInterval(() => {
        registration.update();
      }, 60000 * 60);

    } catch (error) {
      console.error('❌ Service Worker registration failed:', error);
    }
  }

  // Track service worker installation
  private trackInstalling(worker: ServiceWorker): void {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed') {
        if (navigator.serviceWorker.controller) {
          // New update available
          console.log('🆕 New Service Worker available');
          this.config.onUpdate?.(this.registration!);
          this.config.onNeedRefresh?.(this.registration!);
        } else {
          // First install
          console.log('📱 App is ready for offline use');
          this.config.onOfflineReady?.();
          this.config.onSuccess?.(this.registration!);
        }
      }
    });
  }

  // Update service worker
  async updateServiceWorker(): Promise<void> {
    if (this.registration?.waiting) {
      console.log('🔄 Updating Service Worker...');
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      // Reload page after update
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    }
  }

  // Unregister service worker (for development)
  async unregister(): Promise<boolean> {
    if (this.registration) {
      return this.registration.unregister();
    }
    return false;
  }

  // Get cache size and status
  async getCacheInfo(): Promise<{
    caches: string[];
    totalSize: number;
  }> {
    if (!('caches' in window)) {
      return { caches: [], totalSize: 0 };
    }

    const cacheNames = await caches.keys();
    let totalSize = 0;

    for (const cacheName of cacheNames) {
      const cache = await caches.open(cacheName);
      const keys = await cache.keys();
      
      for (const request of keys) {
        const response = await cache.match(request);
        if (response) {
          const blob = await response.blob();
          totalSize += blob.size;
        }
      }
    }

    return {
      caches: cacheNames,
      totalSize
    };
  }

  // Clear all caches
  async clearCaches(): Promise<void> {
    if (!('caches' in window)) {
      return;
    }

    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map(cacheName => caches.delete(cacheName))
    );
    
    console.log('🗑️ All caches cleared');
  }
}

// Create and export service worker manager instance
export const swManager = new ServiceWorkerManager({
  onSuccess: (registration) => {
    console.log('🎉 App is ready for offline use');
    // You can show a toast notification here
  },
  
  onUpdate: (registration) => {
    console.log('🆕 New version available');
    // You can show an update prompt here
    showUpdatePrompt();
  },
  
  onOfflineReady: () => {
    console.log('📱 App cached for offline use');
    // You can show a notification that app works offline
  },
  
  onNeedRefresh: (registration) => {
    console.log('🔄 Please refresh to get the latest version');
  }
});

// Show update prompt to user
function showUpdatePrompt(): void {
  if (window.confirm('New version available! Refresh to update?')) {
    swManager.updateServiceWorker();
  }
}

// Register service worker on page load
export const registerServiceWorker = () => {
  // Only register in production
  if (process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      swManager.register();
    });
  }
};

// Network status utilities
export const NetworkStatus = {
  isOnline: () => navigator.onLine,
  
  onOnline: (callback: () => void) => {
    window.addEventListener('online', callback);
  },
  
  onOffline: (callback: () => void) => {
    window.addEventListener('offline', callback);
  },
  
  // Show network status
  showNetworkStatus: () => {
    const updateOnlineStatus = () => {
      const status = navigator.onLine ? 'online' : 'offline';
      console.log(`🌐 Network status: ${status}`);
      
      // You can show a toast or update UI here
      document.body.classList.toggle('offline', !navigator.onLine);
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    updateOnlineStatus(); // Initial check
  }
};

// Preload critical resources
export const preloadCriticalResources = () => {
  const criticalResources = [
    '/static/js/main.js',
    '/static/css/main.css',
    'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap'
  ];

  criticalResources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource;
    
    if (resource.endsWith('.js')) {
      link.as = 'script';
    } else if (resource.endsWith('.css') || resource.includes('fonts.googleapis')) {
      link.as = 'style';
    }
    
    document.head.appendChild(link);
  });
};